import React from "react";
import {
  matchPath,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import useBootstrapBackdropFix from "./useBootstrapBackdropFix";
import logoutUser from "../../utils/logout";
import {
  getNavigationPermissions,
  hasManagementRouteAccess,
} from "../master/scaiManagementConfig";

// Never allow invalid localStorage JSON to crash route rendering.
function readStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw || raw === "undefined" || raw === "null") return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function normalizePath(path) {
  const value = String(path || "/")
    .split(/[?#]/)[0]
    .trim();
  return value.replace(/\/+$/, "") || "/";
}

function matchesGrantedRoute(routePattern, pathname) {
  if (typeof routePattern !== "string" || !routePattern.trim()) return false;
  const pattern = normalizePath(routePattern);
  try {
    // Match the user's current URL against the stored permission pattern,
    // including routes like /teachers/view/:id.
    return Boolean(matchPath({ path: pattern, end: true }, pathname));
  } catch {
    return false;
  }
}

function AccessDenied({ path }) {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    // A directly opened URL may not have a meaningful previous page.
    if (window.history.length > 1 && location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  };

  const switchAccount = () => {
    // Your existing logout utility should clear authentication and redirect.
    logoutUser();
  };

  return (
    <main className="scai-denied-screen">
      <section
        className="scai-denied-card"
        role="alert"
        aria-labelledby="scai-denied-title"
      >
        <div className="scai-denied-icon" aria-hidden="true">
          <i className="ri-lock-2-line" />
        </div>
        <span className="scai-denied-eyebrow">403 · RESTRICTED ACCESS</span>
        <h1 id="scai-denied-title">You don't have access to this page</h1>
        <p>
          Your account doesn't have permission to view this page. Please contact
          your school administrator to request access.
        </p>
        <div className="scai-denied-path" title={path}>
          <i className="ri-link-m" aria-hidden="true" />
          <span>{path}</span>
        </div>
        <div className="scai-denied-actions">
          <button
            type="button"
            className="scai-denied-primary"
            onClick={goBack}
          >
            <i className="ri-arrow-left-line" aria-hidden="true" /> Go Back
          </button>
          <button
            type="button"
            className="scai-denied-secondary"
            onClick={() => navigate("/")}
          >
            <i className="ri-home-4-line" aria-hidden="true" /> Dashboard
          </button>
        </div>
        <button
          type="button"
          className="scai-denied-switch"
          onClick={switchAccount}
        >
          <i className="ri-logout-box-r-line" aria-hidden="true" />
          Login with another account
        </button>
      </section>
      <style>{`
        .scai-denied-screen {
          --denied-bg: #f7f8fc;
          --denied-surface: #ffffff;
          --denied-text: #252b42;
          --denied-muted: #6f778f;
          --denied-border: #e5e8f1;
          --denied-accent: #696cff;
          min-height: 100dvh;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: var(--denied-bg);
          color: var(--denied-text);
        }
        .scai-denied-card {
          width: min(100%, 520px);
          padding: 42px 32px 32px;
          background: var(--denied-surface);
          border: 1px solid var(--denied-border);
          border-radius: 20px;
          box-shadow: 0 14px 45px rgba(38, 44, 85, .055);
          text-align: center;
        }
        .scai-denied-icon {
          display: grid;
          place-items: center;
          width: 70px;
          height: 70px;
          margin: 0 auto 18px;
          border-radius: 20px;
          background: rgba(105, 108, 255, .10);
          color: var(--denied-accent);
        }
        .scai-denied-icon i { font-size: 33px; line-height: 1; }
        .scai-denied-eyebrow {
          display: inline-block;
          color: var(--denied-accent);
          font-size: 11px;
          font-weight: 800;
          letter-spacing: .1em;
        }
        .scai-denied-card h1 {
          color: var(--denied-text);
          font-size: clamp(21px, 3vw, 26px);
          font-weight: 800;
          line-height: 1.25;
          margin: 12px 0;
        }
        .scai-denied-card p {
          color: var(--denied-muted);
          font-size: 13px;
          line-height: 1.7;
          margin: 0 auto 20px;
          max-width: 430px;
        }
        .scai-denied-path {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-width: 0;
          padding: 12px;
          margin-bottom: 24px;
          color: var(--denied-muted);
          background: var(--denied-bg);
          border: 1px solid var(--denied-border);
          border-radius: 10px;
          font-size: 12px;
        }
        .scai-denied-path span { overflow-wrap: anywhere; }
        .scai-denied-actions { display: flex; justify-content: center; flex-wrap: wrap; gap: 10px; }
        .scai-denied-actions button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 44px;
          padding: 10px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 750;
          cursor: pointer;
          transition: background .15s ease, transform .15s ease;
        }
        .scai-denied-actions button:hover { transform: translateY(-1px); }
        .scai-denied-primary { border: 1px solid var(--denied-accent); background: var(--denied-accent); color: #fff; }
        .scai-denied-secondary { border: 1px solid var(--denied-border); background: var(--denied-surface); color: var(--denied-text); }
        .scai-denied-secondary:hover { background: var(--denied-bg); }
        .scai-denied-switch {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          margin-top: 21px;
          padding: 7px 10px;
          border: 0;
          background: transparent;
          color: var(--denied-accent);
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
        }
        .scai-denied-switch:hover { text-decoration: underline; }
        [data-style="dark"] .scai-denied-screen,
        [data-bs-theme="dark"] .scai-denied-screen {
          --denied-bg: #222335;
          --denied-surface: #2b2c40;
          --denied-text: #e8e9f3;
          --denied-muted: #b2b5c9;
          --denied-border: #484963;
          --denied-accent: #a394ff;
        }
        @media (max-width: 480px) {
          .scai-denied-screen { padding: 14px; }
          .scai-denied-card { padding: 32px 18px 24px; }
          .scai-denied-actions button { flex: 1 1 130px; }
        }
      `}</style>
    </main>
  );
}

export default function PrivateRoute({ children }) {
  useBootstrapBackdropFix();
  const location = useLocation();
  const pathname = normalizePath(location.pathname);

  const userData = readStorage("user_data", {});
  const client = readStorage("client", {});

  // Authentication is separate from route authorization.
  // Authentication is separate from route authorization.
  const loggedIn = Boolean(userData?.remember_token && client?.username);

  if (!loggedIn) {
    return <Navigate to="/user/verify" replace state={{ from: location }} />;
  }

  // Super Admin: role_id = 1 gets access to every frontend route.
  const isSuperAdmin = String(userData?.role_id ?? "") === "1";

  if (isSuperAdmin) {
    return children;
  }

  // Normal staff users must pass route authorization.
  const access = getNavigationPermissions();
  const allowed = hasManagementRouteAccess(pathname, access);

  if (!allowed) {
    return <AccessDenied path={pathname} />;
  }

  return children;
}
